import { z } from 'zod/v4'

import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'

/*
 * The seven readings of «Ingresos», one schema per interface of `contracts/admin/revenue-analytics.types.ts`: same
 * names, same optionality, every object strict. `shared-revenue-screen.type-assertions.ts` ties each schema to its
 * interface in both directions, so the two cannot drift.
 */

export const REVENUE_PERIODS = ['day', 'week', 'month', 'quarter', 'year'] as const
const metricPeriodSchema = z.enum(REVENUE_PERIODS)
const trendDirectionSchema = z.enum(['up', 'down', 'stable'])
const mrrMovementTypeSchema = z.enum(['new', 'expansion', 'contraction', 'churn', 'reactivation'])

export const kpiCardSchema = z.strictObject({
  label: z.string(),
  value: z.number().nullable(),
  formatted: z.string(),
  currency: z.string().optional(),
  previousValue: z.number().optional(),
  trend: z
    .strictObject({
      direction: trendDirectionSchema,
      percentage: z.number(),
      period: z.string(),
    })
    .optional(),
})

export const revenueOverviewSchema = z.strictObject({
  mrr: kpiCardSchema,
  arr: kpiCardSchema,
  churnRate: z.strictObject({ customer: kpiCardSchema, mrr: kpiCardSchema }),
  customers: kpiCardSchema,
  arpu: kpiCardSchema,
  ltv: kpiCardSchema,
  period: metricPeriodSchema,
  generatedAt: z.string(),
  stripe: z.strictObject({
    status: z.enum(['ok', 'unavailable', 'not_configured']),
    fetchedAt: z.string().nullable(),
    stale: z.boolean(),
    errorType: z.string().nullable(),
    refreshThrottled: z.boolean().optional(),
  }),
})

export const mrrMovementSchema = z.strictObject({
  type: mrrMovementTypeSchema,
  amount: z.number(),
  currency: z.string(),
  count: z.number(),
  percentage: z.number(),
})

export const mrrBreakdownSchema = z.strictObject({
  startingMRR: z.number(),
  new: mrrMovementSchema,
  expansion: mrrMovementSchema,
  contraction: mrrMovementSchema,
  churn: mrrMovementSchema,
  reactivation: mrrMovementSchema,
  endingMRR: z.number(),
  netChange: z.number(),
  netChangePercentage: z.number(),
  currency: z.string(),
  period: z.strictObject({ start: z.string(), end: z.string(), type: metricPeriodSchema }),
})

export const revenueDataPointSchema = z.strictObject({
  date: z.string(),
  mrr: z.number(),
  arr: z.number(),
  revenue: z.number(),
  customers: z.number(),
  arpu: z.number(),
})

export const revenueTrendSchema = z.strictObject({
  data: z.array(revenueDataPointSchema),
  period: metricPeriodSchema,
  startDate: z.string(),
  endDate: z.string(),
  currency: z.string(),
})

export const churnByPlanSchema = z.strictObject({
  planId: z.string(),
  planName: z.string(),
  customersAtStart: z.number(),
  customersChurned: z.number(),
  churnRate: z.number(),
  mrrAtStart: z.number(),
  mrrChurned: z.number(),
  mrrChurnRate: z.number(),
})

export const churnAnalysisSchema = z.strictObject({
  customerChurnRate: z.number(),
  previousCustomerChurnRate: z.number().optional(),
  mrrChurnRate: z.number(),
  previousMrrChurnRate: z.number().optional(),
  totalCustomers: z.number(),
  churnedCustomers: z.number(),
  totalMRR: z.number(),
  churnedMRR: z.number(),
  byPlan: z.array(churnByPlanSchema),
  byMonth: z.array(
    z.strictObject({ month: z.string(), customersChurned: z.number(), mrrChurned: z.number() }),
  ),
  period: z.strictObject({ start: z.string(), end: z.string() }),
})

export const funnelStepSchema = z.strictObject({
  name: z.string(),
  count: z.number(),
  percentage: z.number(),
  dropOffFromPrevious: z.number(),
})

export const conversionFunnelSchema = z.strictObject({
  totalLeads: z.number(),
  steps: z.strictObject({
    free: funnelStepSchema,
    trial: funnelStepSchema,
    paid: funnelStepSchema,
    churned: funnelStepSchema,
  }),
  conversionRates: z.strictObject({
    freeToPaid: z.number(),
    trialToPaid: z.number(),
    overall: z.number(),
  }),
  period: z.strictObject({ start: z.string(), end: z.string() }),
})

export const revenueByPlanSchema = z.strictObject({
  planId: z.string(),
  planName: z.string(),
  planType: z.string(),
  customers: z.number(),
  mrr: z.number(),
  arr: z.number(),
  percentageOfTotal: z.number(),
  averageRevenuePerCustomer: z.number(),
})

export const revenueByPlanResponseSchema = z.strictObject({
  totalMRR: z.number(),
  totalARR: z.number(),
  totalCustomers: z.number(),
  plans: z.array(revenueByPlanSchema),
  currency: z.string(),
  period: z.string(),
})

export const cohortDataSchema = z.strictObject({
  cohort: z.string(),
  cohortSize: z.number(),
  retention: z.array(
    z.strictObject({
      period: z.number(),
      customers: z.number(),
      percentage: z.number(),
      revenue: z.number(),
      mrrRetention: z.number(),
    }),
  ),
})

export const cohortAnalysisSchema = z.strictObject({
  cohorts: z.array(cohortDataSchema),
  averageRetention: z.strictObject({ byPeriod: z.array(z.number()), overall: z.number() }),
  period: metricPeriodSchema,
})

/** Each of the seven readings fails on its own (`screenSection`). `period` moves the overview and the trend; the other five readings keep the windows the old routes defaulted to. */
export const revenueQuerySchema = z.strictObject({
  period: metricPeriodSchema.default('month'),
})

export const adminRevenueScreen = defineScreen({
  id: 'admin.revenue',
  path: '/api/screens/admin/revenue',
  access: 'super_admin',
  query: revenueQuerySchema,
  response: z.strictObject({
    overview: screenSection(revenueOverviewSchema),
    mrr: screenSection(mrrBreakdownSchema),
    trend: screenSection(revenueTrendSchema),
    churn: screenSection(churnAnalysisSchema),
    conversion: screenSection(conversionFunnelSchema),
    byPlan: screenSection(revenueByPlanResponseSchema),
    cohorts: screenSection(cohortAnalysisSchema),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.revenue'],
  budget: { requests: 1, statements: 30, bytes: 80_000, p95Ms: 800 },
  summary: 'Revenue: KPIs, MRR movements, trend, churn, conversion, revenue by plan and cohorts',
})
export type AdminRevenueQuery = ScreenQuery<typeof adminRevenueScreen>
export type AdminRevenueData = ScreenData<typeof adminRevenueScreen>
